import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  fetchAsaasSubaccountsOverview,
  createAsaasSubaccount,
  fetchAsaasSubaccountDetail,
  updateAsaasSubaccount,
  transferAsaasOwnership,
  deleteAsaasSubaccount,
  fetchAsaasWebhooks,
  activateAsaasWebhook,
  deleteAsaasWebhook,
} from "./thunks";
import { toast } from "react-toastify";
import type { AsaasSubaccountOverviewItem, AsaasWebhookInfo } from "@/types";

interface OverviewPagination {
  total: number;
  per_page: number;
  current_page: number;
  last_page: number;
  from: number;
  to: number;
  has_more: boolean;
}
interface OverviewState {
  data: AsaasSubaccountOverviewItem[];
  pagination: OverviewPagination | null;
  applied_filters: Record<string, any>;
  sort: {
    field: string;
    direction: "asc" | "desc";
    allowed: string[];
  } | null;
  loading: boolean;
  error: string | null;
  startedAt?: number | null;
  lastFetchedAt?: number | null;
}

interface DetailMap {
  [establishmentId: string]: any;
}
interface WebhookMap {
  [establishmentId: string]: AsaasWebhookInfo[];
}

interface AsaasAdminState {
  overview: OverviewState;
  details: DetailMap;
  webhooks: WebhookMap;
  mutating: boolean;
}

const initialState: AsaasAdminState = {
  overview: {
    data: [],
    pagination: null,
    applied_filters: {},
    sort: null,
    loading: false,
    error: null,
    startedAt: null,
    lastFetchedAt: null,
  },
  details: {},
  webhooks: {},
  mutating: false,
};

const AsaasAdminSlice = createSlice({
  name: "asaasAdmin",
  initialState,
  reducers: {
    clearOverview: (state) => {
      state.overview = initialState.overview;
    },
    forceClearOverviewLoading: (state) => {
      if (state.overview.loading) {
        state.overview.loading = false;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // OVERVIEW
      .addCase(fetchAsaasSubaccountsOverview.pending, (state) => {
        state.overview.loading = true;
        state.overview.error = null;
        state.overview.startedAt = Date.now();
      })
      .addCase(
        fetchAsaasSubaccountsOverview.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.overview.loading = false;
          state.overview.lastFetchedAt = Date.now();
          // Support both legacy shape { content: { data, pagination, ... } } and current { data: { data: [], pagination, ... } }

          const payload = action.payload || {};

          // Normalize several possible backend shapes:
          // 1) { content: { data: [...], pagination, ... } }
          // 2) { data: { data: [...], pagination, ... } }
          // 3) { data: [...] }
          // 4) direct array: [...] (legacy)
          const container = payload.content ?? payload.data ?? payload;

          let list: any[] = [];
          let pagination: any = null;
          let sort: any = null;
          let applied_filters: any = {};

          if (Array.isArray(container)) {
            // payload is array or payload.data is array
            list = container;
          } else if (container && typeof container === "object") {
            // container may itself be the paginated envelope or a direct object
            if (Array.isArray(container.data)) {
              list = container.data;
            } else {
              // some APIs return items under other keys (fallback to empty list)
              list = [];
            }
            pagination = container.pagination ?? container.meta ?? null;
            sort = container.sort ?? null;
            applied_filters = Array.isArray(container.applied_filters)
              ? {}
              : (container.applied_filters ?? {});
          }

          state.overview.data = Array.isArray(list) ? list : [];
          state.overview.pagination = pagination;
          state.overview.sort = sort;
          state.overview.applied_filters = applied_filters;
        },
      )
      .addCase(fetchAsaasSubaccountsOverview.rejected, (state, action: any) => {
        state.overview.loading = false;
        state.overview.lastFetchedAt = Date.now();
        state.overview.error =
          action.payload?.error || "Erro ao carregar overview";
      })
      // DETAIL
      .addCase(
        fetchAsaasSubaccountDetail.fulfilled,
        (state, action: PayloadAction<any>) => {
          const { establishmentId, ...rest } = action.payload;
          state.details[String(establishmentId)] = rest;
        },
      )
      // CREATE
      .addCase(createAsaasSubaccount.pending, (state) => {
        state.mutating = true;
      })
      .addCase(
        createAsaasSubaccount.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.mutating = false;
          toast.success("Subconta criada com sucesso");
        },
      )
      .addCase(createAsaasSubaccount.rejected, (state, action: any) => {
        state.mutating = false;
        toast.error(action.payload?.error || "Erro ao criar subconta");
      })
      // UPDATE
      .addCase(updateAsaasSubaccount.pending, (state) => {
        state.mutating = true;
      })
      .addCase(
        updateAsaasSubaccount.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.mutating = false;
          toast.success("Subconta atualizada");
        },
      )
      .addCase(updateAsaasSubaccount.rejected, (state, action: any) => {
        state.mutating = false;
        toast.error(action.payload?.error || "Erro ao atualizar");
      })
      // TRANSFER
      .addCase(transferAsaasOwnership.pending, (state) => {
        state.mutating = true;
      })
      .addCase(transferAsaasOwnership.fulfilled, (state) => {
        state.mutating = false;
        toast.success("Titularidade transferida");
      })
      .addCase(transferAsaasOwnership.rejected, (state, action: any) => {
        state.mutating = false;
        toast.error(action.payload?.error || "Erro na transferência");
      })
      // DELETE SUBACCOUNT
      .addCase(deleteAsaasSubaccount.pending, (state) => {
        state.mutating = true;
      })
      .addCase(
        deleteAsaasSubaccount.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.mutating = false;
          toast.success("Subconta removida");
        },
      )
      .addCase(deleteAsaasSubaccount.rejected, (state, action: any) => {
        state.mutating = false;
        toast.error(action.payload?.error || "Erro ao remover");
      })
      // WEBHOOKS LIST
      .addCase(
        fetchAsaasWebhooks.fulfilled,
        (state, action: PayloadAction<any>) => {
          const { establishmentId, ...rest } = action.payload;
          // Backend returned shape example:
          // { status: 'success', message: '...', data: { object: 'list', data: [ { id, ... } ] } }
          // We normalize to an array of webhook items only.
          let list: any[] = [];
          // Direct array (legacy?)
          if (Array.isArray((rest as any).webhooks))
            list = (rest as any).webhooks;
          else if (Array.isArray((rest as any).data))
            list = (rest as any).data; // sometimes data is already the array
          else if ((rest as any).data && Array.isArray((rest as any).data.data))
            list = (rest as any).data.data; // nested data.data
          state.webhooks[String(establishmentId)] = list;
        },
      )
      // ACTIVATE WEBHOOK
      .addCase(
        activateAsaasWebhook.fulfilled,
        (state, action: PayloadAction<any>) => {
          toast.success("Webhook ativado");
        },
      )
      .addCase(activateAsaasWebhook.rejected, (_state, action: any) => {
        toast.error(action.payload?.error || "Erro ao ativar webhook");
      })
      // DELETE WEBHOOK
      .addCase(
        deleteAsaasWebhook.fulfilled,
        (state, action: PayloadAction<any>) => {
          const { establishmentId, webhookId } = action.payload;
          const list = state.webhooks[String(establishmentId)] || [];
          const filtered = list.filter(
            (w) => String(w.id) !== String(webhookId),
          );
          state.webhooks[String(establishmentId)] = filtered;
          toast.success("Webhook removido");
        },
      )
      .addCase(deleteAsaasWebhook.rejected, (_state, action: any) => {
        toast.error(action.payload?.error || "Erro ao remover webhook");
      });
  },
});

export const { clearOverview, forceClearOverviewLoading } =
  AsaasAdminSlice.actions;
export default AsaasAdminSlice.reducer;
